import {
	Box,
	Button,
	Flex,
	Icon,
	Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { MdDownload } from 'react-icons/md';
import Card from '../../../components/card/Card';
import { exportLeilaoPurchases } from '../../../services/api/leilao/leilao.datric.api';
import { toastService } from '../../../services/toast.service';
import { fetchLeilaoPurchases } from '../../../store/features/leilao/leilao.slice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';
import { formatPrice } from '../../../utils/format';

export default function MinhasCompras() {
	const dispatch = useAppDispatch();
	const { purchases, purchasesMeta, loadingPurchases } = useAppSelector((state: RootState) => state.leilao);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const [page, setPage] = useState(1);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		dispatch(fetchLeilaoPurchases({ page, limit: 20 }));
	}, [dispatch, page]);

	// O XLSX chega pronto do servidor (streaming); aqui só disparamos o download.
	async function handleExport() {
		try {
			setExporting(true);
			const blob = await exportLeilaoPurchases();
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `leads-comprados-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao exportar os leads');
		} finally {
			setExporting(false);
		}
	}

	return (
		<Card>
			<Flex justify='space-between' align='center' mb='12px' gap='12px' flexWrap='wrap'>
				<Box>
					<Text fontWeight='800' fontSize='lg' color={textColor}>
						Leads comprados
					</Text>
					<Text color={subColor} fontSize='sm'>
						{purchasesMeta ? `${purchasesMeta.total} compra(s)` : ''}
					</Text>
				</Box>
				<Button
					size='sm'
					variant='outline'
					leftIcon={<Icon as={MdDownload} />}
					isLoading={exporting}
					isDisabled={!purchases.length}
					onClick={handleExport}>
					Exportar XLSX
				</Button>
			</Flex>

			{loadingPurchases ? (
				<Flex justify='center' py='40px'>
					<Spinner color='brand.500' />
				</Flex>
			) : purchases.length === 0 ? (
				<Text color={subColor} fontSize='sm' py='20px'>
					Você ainda não comprou nenhum lead no leilão.
				</Text>
			) : (
				<Box overflowX='auto'>
					<Table variant='simple' size='sm'>
						<Thead>
							<Tr>
								<Th>Nome</Th>
								<Th>CPF</Th>
								<Th>Telefone</Th>
								<Th isNumeric>Margem</Th>
								<Th>Bancos</Th>
								<Th>Data do lead</Th>
								<Th>Comprado em</Th>
								<Th isNumeric>Pago</Th>
							</Tr>
						</Thead>
						<Tbody>
							{purchases.map(p => (
								<Tr key={p.id}>
									<Td>{p.data?.nome || '—'}</Td>
									<Td>{p.cpf}</Td>
									<Td>{p.data?.telefone || '—'}</Td>
									<Td isNumeric>
										{p.data?.margemDisponivel != null ? formatPrice(p.data.margemDisponivel) : '—'}
									</Td>
									<Td>{(p.data?.bancosElegiveis || []).join(', ') || '—'}</Td>
									<Td>{format(new Date(p.leadDate), 'dd/MM/yyyy')}</Td>
									<Td>{format(new Date(p.createdAt), 'dd/MM/yyyy HH:mm')}</Td>
									<Td isNumeric>{formatPrice(p.price)}</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				</Box>
			)}

			<Flex gap='8px' align='center' mt='16px'>
				<Button
					size='sm'
					variant='outline'
					isDisabled={!purchasesMeta?.prev || loadingPurchases}
					onClick={() => setPage(p => Math.max(1, p - 1))}>
					Anterior
				</Button>
				<Text fontSize='sm' color={subColor}>
					Página {purchasesMeta?.currentPage ?? 1}
				</Text>
				<Button
					size='sm'
					variant='outline'
					isDisabled={!purchasesMeta?.next || loadingPurchases}
					onClick={() => setPage(p => p + 1)}>
					Próxima
				</Button>
			</Flex>
		</Card>
	);
}
